import { useEffect, useMemo } from 'react';
import type { MouseEvent } from 'react';
import debounce from 'lodash.debounce';
import { Select, Tag } from 'antd';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { useTranslation } from 'react-i18next';
import { IProduct } from '@/models/products.model';
import { useAppDispatch, useAppState } from '@/store';
import { getProduct, getProductsList } from '@/modules/Products';
import { productsSelector } from '@/modules/Products/feature/selectors';
import { productPrimaryImageUrl } from '../../helpers/productPreviewMedia';

type TProductSkusFieldProps = {
  value: unknown;
  onChange: (v: string[]) => void;
};

const ProductSkusField = ({ value, onChange }: TProductSkusFieldProps) => {
  const { t } = useTranslation(['contentPages']);
  const dispatch = useAppDispatch();
  const { productsList } = useAppState(productsSelector);
  const ids = Array.isArray(value) ? (value as string[]) : [];

  const debouncedSearch = useMemo(
    () =>
      debounce((q: string) => {
        const trimmed = q.trim();
        const params =
          trimmed === ''
            ? { limit: 40 }
            : /^[\w.-]+$/i.test(trimmed) && trimmed.length <= 64
            ? { sku: trimmed, limit: 40 }
            : { name: trimmed, limit: 40 };
        void dispatch(getProductsList(params));
      }, 400),
    [dispatch],
  );

  useEffect(() => {
    void dispatch(getProductsList({ limit: 40 }));
    return () => debouncedSearch.cancel();
  }, [debouncedSearch, dispatch]);

  const productById = useMemo(() => {
    const m = new Map<string, IProduct>();
    (productsList?.data ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [productsList?.data]);

  useEffect(() => {
    ids.slice(0, 12).forEach((id) => {
      if (!id || productById.has(id)) return;
      void dispatch(getProduct(id));
    });
  }, [dispatch, ids, productById]);

  const options = useMemo(() => {
    const fromApi = (productsList?.data ?? []).map((p) => {
      const src = productPrimaryImageUrl(p);
      return {
        value: p.id,
        label: (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0' }}>
            {src ? (
              <img
                alt=""
                src={src}
                style={{
                  width: 40,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 6,
                  border: '1px solid #f0f0f0',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 6,
                  background: '#f5f5f5',
                  border: '1px solid #f0f0f0',
                  flexShrink: 0,
                }}
              />
            )}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 600, lineHeight: 1.25, wordBreak: 'break-word' }}>{p.name ?? '—'}</div>
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginTop: 2 }}>SKU: {p.sku}</div>
            </div>
          </div>
        ),
      };
    });
    const byId = new Map(fromApi.map((o) => [o.value, o]));
    ids.forEach((id) => {
      if (id && !byId.has(id)) {
        byId.set(id, {
          value: id,
          label: (
            <div style={{ padding: '4px 0' }}>
              <div style={{ fontWeight: 600 }}>{id}</div>
            </div>
          ),
        });
      }
    });
    return Array.from(byId.values());
  }, [productsList?.data, ids]);

  const tagRender = (props: CustomTagProps) => {
    const { value: tagVal, closable, onClose } = props;
    const id = String(tagVal ?? '');
    const p = productById.get(id);
    const thumb = p ? productPrimaryImageUrl(p) : undefined;
    const onPreventMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        onMouseDown={onPreventMouseDown}
        style={{ marginRight: 4, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}
      >
        {thumb ? (
          <img
            alt=""
            src={thumb}
            width={20}
            height={20}
            style={{ borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
          />
        ) : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
          {p?.name ? (
            <>
              <strong>{p.name}</strong>
              <span style={{ fontWeight: 400, opacity: 0.7 }}> · {p.sku}</span>
            </>
          ) : (
            id
          )}
        </span>
      </Tag>
    );
  };

  return (
    <Select
      mode="multiple"
      style={{ width: '100%' }}
      placeholder={t('field_skus_search_placeholder')}
      showSearch
      filterOption={false}
      onSearch={(q) => debouncedSearch(q)}
      options={options}
      value={ids}
      onChange={(v) => onChange(v as string[])}
      tagRender={tagRender}
      popupMatchSelectWidth={false}
      dropdownStyle={{ minWidth: 320 }}
    />
  );
};

export default ProductSkusField;
